// QuestionHistory.js - Enhanced with more detailed answer tracking
import React, { useState, useEffect } from "react";
import "./QuestionHistory.css";

const QuestionHistory = ({ askedQuestions, onClearHistory, onClose, updateFlag }) => {
  const [filter, setFilter] = useState("");
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [renderedQuestions, setRenderedQuestions] = useState([]);

  // Process questions whenever askedQuestions or updateFlag changes
  useEffect(() => {
    // Sort questions by timestamp (newest first)
    const sorted = [...askedQuestions].sort((a, b) =>
      ((b.timestamp || 0) - (a.timestamp || 0))
    );

    // Apply filtering
    const filtered = sorted.filter(q => {
      const questionText = getQuestionText(q);
      return questionText.toLowerCase().includes(filter.toLowerCase());
    });

    setRenderedQuestions(filtered);

    // Log for debugging
    console.log(`Rendering ${filtered.length} questions, updateFlag: ${updateFlag}`);

  }, [askedQuestions, filter, updateFlag]);

  // Safe method to get question text
  const getQuestionText = (question) => {
    if (typeof question === 'string') return question;
    if (typeof question === 'object') {
      return question.text ||
             question.question ||
             "Unnamed Question";
    }
    return "Unknown Question";
  };

  // Format the timestamp
  const formatDate = (timestamp) => {
    if (!timestamp) return "Unknown date";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Determine answer status for a question - improved to better detect correct/incorrect/skipped
  const getAnswerStatus = (question) => {
    if (typeof question !== 'object') return "";

    // Debug logging to see what's in the question object
    console.log("Question status check:", {
      isSkipped: question.isSkipped,
      isCorrect: question.isCorrect,
      userAnswer: question.userAnswer,
      correctAnswer: question.correctAnswer
    });

    // Check if the question was explicitly marked as skipped
    if ('isSkipped' in question && question.isSkipped === true) {
      return "skipped";
    }

    // Check if the userAnswer is "SKIPPED" (for backward compatibility)
    if (question.userAnswer === "SKIPPED") {
      return "skipped";
    }

    // First check for explicit isCorrect property
    if ('isCorrect' in question) {
      // Make sure we handle null/undefined values properly
      if (question.isCorrect === true) return "correct";
      if (question.isCorrect === false) return "incorrect";
    }

    // If no explicit isCorrect property, try to determine from other properties
    if (question.userAnswer && question.correctAnswer) {
      const normalizedUserAnswer = question.userAnswer.trim().toUpperCase();
      const normalizedCorrectAnswer = question.correctAnswer.trim().toUpperCase();
      return normalizedUserAnswer === normalizedCorrectAnswer ? "correct" : "incorrect";
    }

    // If we can't determine, return empty string
    return "";
  };

  // Debug function to inspect question object
  const debugQuestion = (q) => {
    console.log("Question Object:", q);
    console.log("isCorrect property:", q.isCorrect);
    console.log("User answer:", q.userAnswer);
    console.log("Correct answer:", q.correctAnswer);
    console.log("Status:", getAnswerStatus(q));
  };

  return (
    <div className="history-modal">
      <div className="history-content">
        <div className="history-header">
          <h2>Question History</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="history-tools">
          <input
            type="text"
            placeholder="Search questions..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="history-search"
          />
          <button
            onClick={onClearHistory}
            className="history-clear-button"
          >
            Clear History
          </button>
        </div>

        <div className="history-list">
          {renderedQuestions.length === 0 ? (
            <div className="history-empty">No questions found.</div>
          ) : (
            renderedQuestions.map((q, index) => {
              // Determine answer status
              const answerStatus = getAnswerStatus(q);
              
              // Debug log for each rendered question
              console.log(`Rendering question ${index}:`, {
                text: getQuestionText(q).substring(0, 30),
                status: answerStatus,
                className: `history-item ${answerStatus}`
              });

              return (
                <div
                  key={index}
                  className={`history-item ${answerStatus}`}
                  onClick={() => {
                    setSelectedQuestion(q);
                    // Uncomment for debugging
                    debugQuestion(q);
                  }}
                >
                  <div className="history-question">
                    {getQuestionText(q)}
                    {answerStatus && (
                      <span className={`answer-badge ${answerStatus}`}>
                        {answerStatus === "correct" ? "Correct" :
                         answerStatus === "incorrect" ? "Incorrect" :
                         "Skipped"}
                      </span>
                    )}
                  </div>
                  {q.timestamp && (
                    <div className="history-timestamp">{formatDate(q.timestamp)}</div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Question Details Modal */}
        {selectedQuestion && (
          <div className="question-details-modal">
            <div className="question-details-content">
              <div className="details-header">
                <h3>Question Details</h3>
                <button className="close-button" onClick={() => setSelectedQuestion(null)}>×</button>
              </div>

              <div className="details-body">
                <div className="detail-question">{getQuestionText(selectedQuestion)}</div>

                {/* Choices rendering with user's answer and correct answer */}
                {selectedQuestion.choices && (
                  <div className="detail-choices">
                    {selectedQuestion.choices.map((choice, idx) => {
                      const isCorrect = choice.label === selectedQuestion.correctAnswer;
                      const isSelected = choice.label === selectedQuestion.userAnswer;

                      return (
                        <div
                          key={idx}
                          className={`detail-choice
                            ${isCorrect ? 'correct' : ''}
                            ${isSelected && !isCorrect ? 'incorrect' : ''}
                            ${isSelected ? 'selected' : ''}`}
                        >
                          <span className="choice-label">{choice.label}</span>
                          <span className="choice-text">{choice.text}</span>
                          {isCorrect && <span className="choice-mark correct">✓</span>}
                          {isSelected && !isCorrect && <span className="choice-mark incorrect">✗</span>}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Detailed answer information */}
                <div className="detail-answer">
                  <p>
                    <strong>Correct Answer:</strong>{' '}
                    {selectedQuestion.correctAnswer || 'Not specified'}
                  </p>
                  {selectedQuestion.userAnswer && (
                    <p>
                      <strong>Your Answer:</strong>{' '}
                      <span className={
                        selectedQuestion.isCorrect ? 'text-green-600' : 'text-red-600'
                      }>
                        {selectedQuestion.userAnswer}
                      </span>
                    </p>
                  )}
                </div>

                {/* Explanation */}
                {selectedQuestion.explanation && (
                  <div className="detail-explanation">
                    <h4>Explanation</h4>
                    <p>{selectedQuestion.explanation}</p>
                  </div>
                )}

                <div className="detail-timestamp">
                  Asked on: {formatDate(selectedQuestion.timestamp)}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionHistory;